import { environment } from 'environments/environment';
import { logger } from 'utils/logger';
import { apiRequest } from 'features/data-management/utils/apiRequest';

const log = logger.getLogger('PlcSectionsService');

export type PlcSection = 'TODOS' | 'TOOLKIT' | 'METRICS' | 'HELP';
export type PlcSectionState = 'ENABLED' | 'DISABLED' | 'HIDDEN';

export interface PlcSectionAccess {
  section: PlcSection;
  state: PlcSectionState;
  message: string | null;
}

export const PLC_SECTION_LABELS: Record<PlcSection, string> = {
  TODOS: 'To-Do List',
  TOOLKIT: 'Toolkit',
  METRICS: 'Campaign Metrics',
  HELP: 'Help Center',
};

export const PLC_SECTIONS = Object.keys(PLC_SECTION_LABELS) as PlcSection[];

// Only these account types can hold PLC programs (AGG covers aggregators — e.g. WelcomeTech,
// whose hidden metrics are this panel's main use case; STA is being set up)
export const PLC_PROGRAM_LINES_OF_BUSINESS = ['AFF', 'STA', 'AGG'];

export const accountSupportsPlc = (lineOfBusiness?: string | null): boolean =>
  !!lineOfBusiness && PLC_PROGRAM_LINES_OF_BUSINESS.includes(lineOfBusiness);

const sectionsUrl = (accountId: number): string => {
  const { baseUrl, basePath } = environment.services.plc;
  return `${baseUrl}${basePath}/programs/${accountId}/sections`;
};

class PlcSectionsService {
  // Not every environment routes plc-svc (e.g. India) — callers hide the panel when unset
  isConfigured(): boolean {
    return !!environment.services?.plc;
  }

  async getSections(accountId: number): Promise<PlcSectionAccess[]> {
    const response = await apiRequest('get', sectionsUrl(accountId), {
      label: 'fetch PLC section access',
      log,
      useCache: false,
    });
    return response?.data || [];
  }

  async setSection(
    accountId: number,
    section: PlcSection,
    state: PlcSectionState,
    message?: string
  ): Promise<void> {
    const trimmed = message?.trim();
    await apiRequest('put', `${sectionsUrl(accountId)}/${section}`, {
      label: 'update PLC section access',
      log,
      data: trimmed ? { state, message: trimmed } : { state },
    });
  }

  async resetSection(accountId: number, section: PlcSection): Promise<void> {
    await apiRequest('delete', `${sectionsUrl(accountId)}/${section}`, {
      label: 'reset PLC section access',
      log,
    });
  }
}

export const plcSectionsService = new PlcSectionsService();
export default plcSectionsService;
